import { queuesForVariation } from '@axle-health/work-queue-definitions';
import { Ordering } from 'gql-types';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import { defaultPatientsRowToLink, defaultVisitsRowToLink } from '../definitions';
import { definitionFor, fromDefinition } from '../fromDefinition';
import { PatientDashboardContainer, VisitDashboardContainer } from '../types';
import { COFK_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

// Care Options for Kids is a pediatric-therapy organization (OT / PT / SLP). It does
// not have episodes of care enabled, so its row links are episode-less.

const PUBLISHED_QUEUES = queuesForVariation('CARE_OPTIONS_FOR_KIDS');

const EVAL_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, COFK_CONTAINER_KEYS.eval),
    {
        defaultEnabled: true,
        hoverText: 'Evals that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Eval visits to schedule.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const PATIENTS_NEEDING_SCHEDULING_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, COFK_CONTAINER_KEYS.patientsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Patients with visits that need to be scheduled.',
        tableConfig: {
            rowToLink: defaultPatientsRowToLink,
            emptyTableText: 'No patients to schedule.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

// The row is a pending visit that needs canceling, not the discharge itself.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            COFK_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
        ),
        {
            defaultEnabled: true,
            hoverText:
                'Discipline discharged patients with scheduled visits that need to be canceled.',
            extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultVisitsRowToLink,
                emptyTableText: 'No pending visits after discipline discharge.',
                ordering: { effectiveDate: Ordering.ASC },
            },
        },
    );

const VISITS_MISMATCHING_PATIENT_PREFERENCE_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, COFK_CONTAINER_KEYS.visitsMismatchingPatientPreference),
    {
        defaultEnabled: true,
        hoverText:
            "Pending visits whose modality doesn't match the patient's strict Telehealth Only or In Person Only preference.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits mismatching patient preference.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const VISIT_SYNC_ISSUES_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, COFK_CONTAINER_KEYS.visitSyncIssues),
    {
        defaultEnabled: true,
        hoverText: 'Visits unable to be synced to EMR.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No sync issues at this time.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

export const CARE_OPTIONS_FOR_KIDS_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        EVAL_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        VISITS_MISMATCHING_PATIENT_PREFERENCE_CONTAINER,
        VISIT_SYNC_ISSUES_CONTAINER,
    ],
};
